import { API_BASE_URL, authFetch } from './api';

// === Helper ===


// === Types ===

export interface StartGroupAgentsRequest {
  task?: string;
  pad_ids: string[];
  max_steps?: number;
  persona?: string;
  disable_proactive_search?: boolean;
  return_home_on_finish?: boolean;
  llm_model?: string;
  comment_llm_model?: string;
  brand_id?: string;
  slack_bot_id?: string;
  capture_required?: boolean;
  // Device-profile auto-apply
  engagement?: string;
  platform?: string;
  prompt_id?: string;
  max_comments_per_session?: number;
  max_searches_per_session?: number;
}

export interface RunPreviewEntry {
  pad_id: string;
  device_name: string;
  template_id: string;
  template_name: string;
  engagement: string | null;
  platform: string | null;
}

export interface StartGroupAgentsResponse {
  group_id: string;
  task: string;
  pad_ids: string[];
  agent_ids: Record<string, string>;
  failures: Record<string, string>;
}

export interface GroupRunStatusResponse {
  group_id: string;
  user_id: string;
  task: string;
  pad_ids: string[];
  agent_ids: Record<string, string>;
  agent_statuses: Record<string, string>;
  failures: Record<string, string>;
  status: string;
}

export interface SessionDetail {
  group_id: string;
  task: string;
  persona: string | null;
  max_steps: number;
  pad_ids: string[];
  agent_ids: Record<string, string>;
  agent_statuses: Record<string, string>;
  failures: Record<string, string>;
  device_step_counts: Record<string, number>;
  device_count: number;
  devices_succeeded: number;
  devices_failed: number;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
}

// === API Functions ===

const BASE = `${API_BASE_URL}/vmos-agentic-group`;

export async function startGroupAgents(request: StartGroupAgentsRequest): Promise<StartGroupAgentsResponse> {
  const res = await authFetch(`${BASE}/agent/start`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return res.json();
}

export async function previewGroupAgents(
  padIds: string[],
  engagement: string,
  platform?: string,
): Promise<{ preview: RunPreviewEntry[] }> {
  const res = await authFetch(`${BASE}/agent/preview`, {
    method: 'POST',
    body: JSON.stringify({ pad_ids: padIds, engagement, platform: platform || undefined }),
  });
  return res.json();
}

export async function stopGroupAgents(groupId: string): Promise<any> {
  const res = await authFetch(`${BASE}/agent/${groupId}/stop`, {
    method: 'POST',
  });
  return res.json();
}

export async function getGroupRunStatus(groupId: string): Promise<GroupRunStatusResponse> {
  const res = await authFetch(`${BASE}/agent/${groupId}/status`);
  return res.json();
}

export async function getSessionDetail(groupId: string): Promise<SessionDetail> {
  const res = await authFetch(`${BASE}/sessions/${groupId}`);
  return res.json();
}

export function getGroupStreamUrl(groupId: string): string {
  return `${BASE}/agent/${groupId}/stream`;
}

export interface GroupStateDevice {
  status: string;
  step: number;
  max_steps: number;
  device_name: string;
}

export interface GroupStateResponse {
  group_id: string;
  devices: Record<string, GroupStateDevice>;
}

export async function getGroupState(groupId: string): Promise<GroupStateResponse> {
  const res = await authFetch(`${BASE}/agent/${groupId}/state`);
  return res.json();
}

export interface ActiveRunDevice {
  status: string;
  step: number;
  max_steps: number;
  device_name: string;
}

export interface AccountIssue {
  issue_type?: string | null;
  platform?: string | null;
  detail?: string | null;
}

export interface ActiveRun {
  group_id: string;
  user_id: string;
  managed_for_user_ids: string[];
  account_issues?: Record<string, AccountIssue>;
  task: string;
  persona: string | null;
  pad_ids: string[];
  agent_statuses: Record<string, string>;
  failures: Record<string, string>;
  status: string;
  started_at: number;
  devices: Record<string, ActiveRunDevice>;
}

export interface ActiveRunsResponse {
  runs: ActiveRun[];
}

export interface SessionSummary {
  group_id: string;
  user_id: string;
  managed_for_user_ids: string[];
  task: string;
  persona: string | null;
  device_count: number;
  devices_succeeded?: number | null;
  devices_failed?: number | null;
  account_issues?: Record<string, AccountIssue>;
  status: string;
  duration_seconds: number | null;
  started_at: string;
  completed_at: string | null;
}

export interface SessionsListResponse {
  sessions: SessionSummary[];
  total: number;
  limit: number;
  offset: number;
}

export async function listActiveRuns(): Promise<ActiveRunsResponse> {
  const res = await authFetch(`${BASE}/active`);
  return res.json();
}

export async function listSessions(params: { limit?: number; offset?: number } = {}): Promise<SessionsListResponse> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  const res = await authFetch(`${BASE}/sessions${qs.toString() ? `?${qs}` : ''}`);
  return res.json();
}
